import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Globe, BookOpen, TrendingUp } from 'lucide-react';
import './Home.css';
import { professorData, metrics, institutions, services } from '../utils/mockData';

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content-wrapper">
            <div className="hero-content">
              <div className="hero-badge">
                <Award size={16} />
                <span>Fellow, Royal Society of Arts | Steward, Council for Inclusive Capitalism</span>
              </div>
              <h1 className="hero-title">{professorData.title}</h1>
              <p className="hero-subtitle">{professorData.tagline}</p>
              <p className="hero-description">
                With over two decades advising governments, enterprises, and institutions across four continents, 
                I bring systems-thinking and evidence-based strategy to leaders navigating their most critical decisions.
              </p>
              <div className="hero-cta">
                <Link to="/contact" className="btn btn-primary btn-large">
                  Book Strategic Session
                  <ArrowRight size={20} />
                </Link>
                <Link to="/services" className="btn btn-secondary btn-large">
                  Explore Services
                </Link>
              </div>
            </div>
            <div className="hero-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_7acabb85-1ee6-4146-88a1-f42a2bc441a5/artifacts/9t0p1qqv_JP%20Tamvada%20picture.jpg"
                alt="Professor J.P. Tamvada"
                className="hero-profile-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="metrics-section">
        <div className="container">
          <div className="metrics-grid">
            {metrics.map((metric, index) => (
              <div key={index} className="metric-card">
                <div className="metric-number">{metric.number}</div>
                <div className="metric-label">{metric.label}</div>
                <div className="metric-description">{metric.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who I Serve Section */}
      <section className="serve-section section">
        <div className="container">
          <div className="section-header">
            <h2>Who I Serve</h2>
            <p className="section-subtitle">
              Strategic advisory for leaders navigating complexity at the highest levels
            </p>
          </div>
          <div className="serve-grid">
            <div className="serve-card">
              <div className="serve-icon">
                <Globe size={32} />
              </div>
              <h3>Governments & Policymakers</h3>
              <p>
                Evidence-based policy design, economic development strategies, and institutional innovation 
                that drives measurable societal impact.
              </p>
            </div>
            <div className="serve-card">
              <div className="serve-icon">
                <TrendingUp size={32} />
              </div>
              <h3>Enterprises & Scale-ups</h3>
              <p>
                Strategic growth advisory for organizations at inflection points—navigating market expansion, 
                business model transformation, and sustainable scaling.
              </p>
            </div>
            <div className="serve-card">
              <div className="serve-icon">
                <Users size={32} />
              </div>
              <h3>Institutional Leaders</h3>
              <p>
                Complex problem solving and systems design for universities, research institutions, and 
                development organizations facing multi-dimensional challenges.
              </p>
            </div>
            <div className="serve-card">
              <div className="serve-icon">
                <BookOpen size={32} />
              </div>
              <h3>High-Impact Ventures</h3>
              <p>
                Strategic positioning and venture strategy for founders building companies that aim to 
                transition from startup to enduring enterprise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="services-preview-section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Strategic Engagements</h2>
            <p className="section-subtitle">
              Tailored advisory for complex challenges requiring systems-level thinking
            </p>
          </div>
          <div className="services-preview-grid">
            {services.slice(0, 3).map((service) => (
              <div key={service.id} className="service-preview-card card">
                <h3>{service.title}</h3>
                <p className="service-subtitle">{service.subtitle}</p>
                <p className="service-description">{service.description}</p>
                <div className="service-ideal-for">
                  <span className="ideal-label">Ideal for:</span>
                  <span>{service.idealFor}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="services-cta">
            <Link to="/services" className="btn btn-primary">
              View All Services
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="approach-section section">
        <div className="container">
          <div className="approach-content">
            <div className="approach-text">
              <h2>Why My Approach Is Different</h2>
              <p className="approach-lead">
                Most advisory work treats symptoms. I address systems.
              </p>
              <p>
                Drawing on decades of rigorous research published in the world's leading journals, 
                combined with practical experience advising organizations across continents, I bring 
                a unique perspective that transcends conventional consulting frameworks.
              </p>
              <div className="approach-points">
                <div className="approach-point">
                  <h4>Evidence-Based Strategy</h4>
                  <p>
                    Every recommendation grounded in empirical research and proven frameworks, 
                    not trendy methodologies.
                  </p>
                </div>
                <div className="approach-point">
                  <h4>Systems-Level Thinking</h4>
                  <p>
                    Problems rarely exist in isolation. I map interdependencies and design 
                    interventions that create cascading positive effects.
                  </p>
                </div>
                <div className="approach-point">
                  <h4>Global Perspective</h4>
                  <p>
                    Cross-cultural insights from working across Europe, Americas, Asia, and Africa—bringing 
                    solutions that transcend local limitations.
                  </p>
                </div>
                <div className="approach-point">
                  <h4>Long-Term Value Creation</h4>
                  <p>
                    Strategic guidance focused on sustainable growth and responsible innovation, 
                    not short-term optimization.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Institutions Section */}
      <section className="institutions-section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Institutional Affiliations</h2>
            <p className="section-subtitle">
              Associated with world-leading institutions
            </p>
          </div>
          <div className="institutions-list">
            {institutions.map((institution, index) => (
              <div key={index} className="institution-item">
                <div className="institution-name">{institution.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Navigate Your Strategic Challenge?</h2>
            <p className="cta-text">
              Book a strategic session to discuss how systems-thinking and evidence-based strategy 
              can help you navigate complexity with confidence.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">
                Book Strategic Session
                <ArrowRight size={20} />
              </Link>
              <Link to="/knowledge" className="btn btn-secondary btn-large">
                Explore Insights
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;